import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { startOfMonth, endOfMonth } from "date-fns";

export interface ScheduleChangeRequest {
  id: string;
  schedule_id: string;
  requested_by: string;
  gym_id: string;
  current_day: number;
  current_start_time: string;
  requested_day: number;
  requested_start_time: string;
  requested_end_time: string;
  original_date: string | null;
  replacement_date: string | null;
  status: "pending" | "approved" | "rejected";
  resolved_by: string | null;
  resolved_at: string | null;
  reason: string | null;
  created_at: string;
  requester?: {
    first_name: string | null;
    last_name: string | null;
  };
}

export const useScheduleChangeRequests = (gymId?: string | null, mode: "member" | "professor" = "member") => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const requestsQuery = useQuery({
    queryKey: ["schedule-change-requests", user?.id, gymId, mode],
    queryFn: async (): Promise<ScheduleChangeRequest[]> => {
      let query = supabase
        .from("schedule_change_requests")
        .select("*")
        .order("created_at", { ascending: false });

      if (mode === "member") {
        query = query.eq("requested_by", user!.id);
      }

      const { data, error } = await query;
      if (error) throw error;

      if (mode === "professor" && data && data.length > 0) {
        const requesterIds = [...new Set(data.map(r => r.requested_by))];
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, first_name, last_name")
          .in("id", requesterIds);
        const profileMap = new Map((profiles || []).map(p => [p.id, p]));
        return data.map(r => ({
          ...r,
          status: r.status as "pending" | "approved" | "rejected",
          requester: profileMap.get(r.requested_by) || undefined,
        }));
      }

      return (data || []).map(r => ({ ...r, status: r.status as "pending" | "approved" | "rejected" }));
    },
    enabled: !!user && !!gymId,
  });

  const monthlyCountQuery = useQuery({
    queryKey: ["schedule-change-count", user?.id],
    queryFn: async () => {
      const now = new Date();
      const { count, error } = await supabase
        .from("schedule_change_requests")
        .select("*", { count: "exact", head: true })
        .eq("requested_by", user!.id)
        .gte("created_at", startOfMonth(now).toISOString())
        .lte("created_at", endOfMonth(now).toISOString());
      if (error) throw error;
      return count || 0;
    },
    enabled: !!user && mode === "member",
  });

  const createRequest = useMutation({
    mutationFn: async (data: {
      schedule_id: string;
      gym_id: string;
      current_day: number;
      current_start_time: string;
      requested_day: number;
      requested_start_time: string;
      requested_end_time: string;
      original_date: string;
      replacement_date: string;
      reason?: string;
    }) => {
      const { error } = await supabase
        .from("schedule_change_requests")
        .insert({
          ...data,
          requested_by: user!.id,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Solicitud enviada", description: "Tu solicitud de cambio temporal fue registrada." });
      queryClient.invalidateQueries({ queryKey: ["schedule-change-requests"] });
      queryClient.invalidateQueries({ queryKey: ["schedule-change-count"] });
    },
    onError: () => {
      toast({ title: "Error", description: "No se pudo enviar la solicitud.", variant: "destructive" });
    },
  });

  const resolveRequest = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "approved" | "rejected" }) => {
      const { error } = await supabase
        .from("schedule_change_requests")
        .update({ status, resolved_by: user!.id, resolved_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, { status }) => {
      toast({ title: status === "approved" ? "Aprobado" : "Rechazado" });
      queryClient.invalidateQueries({ queryKey: ["schedule-change-requests"] });
    },
    onError: () => {
      toast({ title: "Error", variant: "destructive" });
    },
  });

  return {
    requests: requestsQuery.data || [],
    isLoading: requestsQuery.isLoading,
    monthlyCount: monthlyCountQuery.data || 0,
    remainingChanges: Math.max(0, 3 - (monthlyCountQuery.data || 0)),
    createRequest,
    resolveRequest,
  };
};
